import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1668952135120-7d997b1b3778?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const CAT_IMG = "https://images.unsplash.com/photo-1777888741000-9100f5190e2d?w=1200";
const VENDOR_IMG = "https://images.unsplash.com/photo-1777628530456-bb93d3a03faf?w=1600";
const FLATLAY = "https://images.unsplash.com/photo-1664333570948-cb94f2985827?w=1200";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    client.get("/products").then(r => setProducts(r.data.slice(0, 8)));
    client.get("/vendors").then(r => setVendors(r.data));
  }, []);

  return (
    <div>
      {/* HERO — asymmetric */}
      <section className="container-editorial pt-8 md:pt-14">
        <div className="grid grid-cols-12 gap-4 md:gap-8 min-h-[80vh]">
          <div className="col-span-12 md:col-span-5 flex flex-col justify-between order-2 md:order-1">
            <div>
              <p className="label-uppercase text-[#B24C38]" data-testid="hero-eyebrow">SS26 Collection · Now live</p>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter mt-6" data-testid="hero-headline">
                Modest, made<br/>with intention.
              </h1>
              <p className="mt-8 text-base md:text-lg text-[#4A4A4A] max-w-md leading-relaxed">
                A marketplace of Jewish-owned ateliers stitching heirloom dresses, everyday layers, and Shabbos-ready menswear — all in one bag.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/shop" className="btn-terracotta" data-testid="hero-cta-shop">Shop the collection <ArrowUpRight className="w-4 h-4" /></Link>
                <Link to="/vendors" className="btn-outline-ink" data-testid="hero-cta-vendors">Meet the vendors</Link>
              </div>
            </div>
            <div className="hidden md:flex gap-8 mt-16 pt-8 border-t border-[#EAE5D9]">
              <div><p className="font-serif text-3xl">{vendors.length || 4}</p><p className="label-uppercase text-[#4A4A4A]">Ateliers</p></div>
              <div><p className="font-serif text-3xl">{products.length ? "12+" : "12+"}</p><p className="label-uppercase text-[#4A4A4A]">Pieces in stock</p></div>
              <div><p className="font-serif text-3xl">100%</p><p className="label-uppercase text-[#4A4A4A]">Jewish-owned</p></div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 relative order-1 md:order-2">
            <div className="w-full h-full min-h-[60vh]">
              <img src={HERO} alt="Modest fashion editorial" className="w-full h-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 bg-[#1A1A1A] text-[#F7F5F0] p-6 max-w-xs">
              <p className="label-uppercase text-[#D4A373]">Featured</p>
              <p className="font-serif text-2xl mt-2 leading-tight">Rivka Midi Dress in Terracotta</p>
              <p className="text-sm mt-2 text-[#D4A373]">Malka Atelier · $248</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee band */}
      <section className="mt-24 md:mt-40 bg-[#1A1A1A] text-[#F7F5F0] py-6 overflow-hidden">
        <div className="flex gap-16 whitespace-nowrap animate-[marquee_30s_linear_infinite]">
          {Array(3).fill(0).map((_,i) => (
            <div key={i} className="flex gap-16">
              <span className="font-serif text-4xl md:text-5xl">Shomer Shabbos ateliers</span>
              <span className="font-serif text-4xl md:text-5xl text-[#D4A373]">·</span>
              <span className="font-serif text-4xl md:text-5xl">Small batch</span>
              <span className="font-serif text-4xl md:text-5xl text-[#D4A373]">·</span>
              <span className="font-serif text-4xl md:text-5xl">Made with kavanah</span>
              <span className="font-serif text-4xl md:text-5xl text-[#D4A373]">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="container-editorial mt-24 md:mt-40">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-uppercase text-[#B24C38]">Browse by category</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3">The Wardrobe</h2>
          </div>
          <Link to="/shop" className="hidden md:inline label-uppercase link-underline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Dresses", img: CAT_IMG },
            { label: "Skirts", img: FLATLAY },
            { label: "Tops", img: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=1000" },
            { label: "Head Coverings", img: "https://images.unsplash.com/photo-1583147247959-1bfe6b7be9d1?w=1000" },
          ].map(cat => (
            <Link key={cat.label} to={`/shop/${cat.label}`} className="group block" data-testid={`category-${cat.label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="product-image-wrap">
                <img src={cat.img} alt={cat.label} />
              </div>
              <p className="font-serif text-2xl md:text-3xl mt-4">{cat.label}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured products */}
      <section className="container-editorial mt-24 md:mt-40">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-uppercase text-[#B24C38]">This week's editorial</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3">New Arrivals</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-14">
          {products.map(p => <ProductCard key={p.id} product={p} testId={`home-product-${p.id}`} />)}
        </div>
      </section>

      {/* Shop by Occasion */}
      <section className="container-editorial mt-24 md:mt-40">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="label-uppercase text-[#B24C38]">Dressed for the moment</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3">Shop by Occasion</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          <Link to="/shop?tag=shabbos" className="group relative block overflow-hidden" data-testid="occasion-shabbos">
            <div className="aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#1A1A1A]">
              <img src="https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=1600" alt="Shabbos clothing"
                   className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"/>
            </div>
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/20 to-transparent">
              <p className="label-uppercase text-[#D4A373]">Erev to Havdalah</p>
              <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter text-[#F7F5F0] mt-2 leading-none">
                Shabbos<br/>Clothing
              </h3>
              <p className="text-[#F7F5F0] mt-4 max-w-md leading-relaxed">
                Long-sleeve midis, tailored suits, and Friday-night pieces you'll reach for every week.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-[#F7F5F0] label-uppercase link-underline w-fit">
                Shop Shabbos →
              </span>
            </div>
          </Link>
          <Link to="/shop?tag=yom-tov" className="group relative block overflow-hidden" data-testid="occasion-yom-tov">
            <div className="aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#B24C38]">
              <img src="https://images.unsplash.com/photo-1777888741000-9100f5190e2d?w=1600" alt="Yom Tov clothing"
                   className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"/>
            </div>
            <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-12 bg-gradient-to-t from-[#B24C38]/85 via-[#B24C38]/25 to-transparent">
              <p className="label-uppercase text-[#F7F5F0]">Chag Sameach</p>
              <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter text-[#F7F5F0] mt-2 leading-none">
                Yom Tov<br/>Clothing
              </h3>
              <p className="text-[#F7F5F0] mt-4 max-w-md leading-relaxed">
                Elevated pieces for Rosh Hashanah, Sukkos, Pesach, Shavuos — dress with kavanah.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-[#F7F5F0] label-uppercase link-underline w-fit">
                Shop Yom Tov →
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Vendors band */}
      <section className="mt-24 md:mt-40 bg-[#EAE5D9]">
        <div className="container-editorial py-24 md:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="label-uppercase text-[#B24C38]">Our ateliers</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3">Every vendor,<br/>Jewish-owned.</h2>
            <p className="mt-6 text-[#4A4A4A] leading-relaxed">We only list stores owned and operated by Jewish makers. Each atelier is vetted for kavanah and craftsmanship.</p>
            <Link to="/vendors" className="btn-ink mt-8 inline-block">Meet them all →</Link>
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {vendors.slice(0,4).map(v => (
                <Link key={v.id} to={`/vendor/${v.slug}`} className="group block bg-[#F7F5F0] p-6 border border-[#EAE5D9]" data-testid={`home-vendor-${v.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <img src={v.banner_image} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <p className="label-uppercase text-[#D4A373]">{v.hechsher}</p>
                  <p className="font-serif text-2xl mt-2">{v.name}</p>
                  <p className="text-sm text-[#4A4A4A] mt-1">{v.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Editorial split */}
      <section className="container-editorial mt-24 md:mt-40 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-6">
          <img src={VENDOR_IMG} alt="Atelier" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col justify-center">
          <p className="label-uppercase text-[#B24C38]">The Story</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-3">
            One bag.<br/>Many ateliers.
          </h2>
          <p className="text-[#4A4A4A] leading-relaxed mt-6 text-lg">
            Traditionally, finding modest clothing meant piecing together orders from a dozen small shops on quiet websites. Kesher brings them into one editorial marketplace — you check out once, we handle the rest.
          </p>
          <div className="mt-8">
            <Link to="/shop" className="btn-terracotta">Start shopping →</Link>
          </div>
        </div>
      </section>

      <style>{`@keyframes marquee { from { transform: translateX(0);} to { transform: translateX(-33.33%);} }`}</style>
    </div>
  );
}
